import { Address } from '@monolith/legacy/types/address';
import { CountryIsoCode } from '@core/types/country';
import { JsonApiResponse } from '@monolith/legacy/types/fulfillment';
import { Currency } from 'dinero.js';
import type { IconName } from '@ankorstore/design-system';

export enum MenuOptions {
  /** @deprecated - used only as redirect route for new separated address and shipping pages. */
  ADDRESSES_AND_SHIPPING = 'brandSettingsAddressesAndShipping',
  ADDRESSES = 'brandSettingsAddresses',
  SHIPPING = 'brandSettingsShipping',
  SHOP_SETTINGS = 'brandSettingsShopSettings',
  COMPANY_DETAILS = 'brandSettingsCompanyDetails',
  BILLING_SETTINGS = 'brandSettingsBillingSettings',
  ACCOUNT_SECURITY = 'brandSettingsAccountSecurity',
  /** @deprecated - kept as redirect to ACCOUNT_SECURITY for old links/emails. */
  COMMUNICATIONS = 'brandSettingsCommunications',
  /** @deprecated - kept as redirect to ACCOUNT_SECURITY for old links/emails. */
  PASSWORD = 'brandSettingsPassword',
  /** @deprecated - kept as redirect to ACCOUNT_SECURITY for old links/emails. */
  EMAIL = 'brandSettingsEmail',
  /** @deprecated - kept as redirect to ACCOUNT_SECURITY for old links/emails. */
  TWO_FACTOR = 'brandSettingsTwoFactor',
}

type MenuOptionsLiteral = `${Exclude<MenuOptions, MenuOptions.ADDRESSES_AND_SHIPPING>}`;

type MenuEntryType = {
  label: string;
  enabled: boolean;
  routeName: string;
  icon?: IconName;
  alert?: boolean;
};

export type MenuEntry = {
  [key in MenuOptionsLiteral]?: MenuEntryType;
};

export enum CountryIso {
  UnitedKingdom = 'GB',
  Germany = 'DE',
}

export enum AddressType {
  Billing = 'billingAddress',
  Public = 'publicAddress',
  Shipping = 'shippingAddress',
}

export interface AddressApiPayload {
  street: string;
  postalCode: string;
  city: string;
  countryCode: CountryIsoCode;
}

export interface BrandShippingSettingsApiPayload {
  averageLeadTime?: string;
  [AddressType.Billing]?: AddressApiPayload;
  [AddressType.Public]?: AddressApiPayload;
  [AddressType.Shipping]?: AddressApiPayload;
  whitelistCountries?: string[];
  eoriNumber?: string;
  lucidNumber?: string;
}

export type UpdateBrandShippingFeeSettingApiPayload = Pick<BrandShippingFeeSetting, 'fee' | 'amount' | 'corridor'>;

export interface BrandShippingSettingsApiResponse {
  averageLeadTime: string;
  [AddressType.Billing]: AddressApiPayload;
  [AddressType.Public]: AddressApiPayload;
  [AddressType.Shipping]: AddressApiPayload;
  whitelistCountries: string[];
  eoriNumber: string;
  lucidNumber: string;
}

export interface BrandHolidaySettingsApiPayload {
  startDate?: Date;
  endDate?: Date;
}
export interface BrandHolidaySettingsApiResponse {
  startDate?: Date;
  endDate?: Date;
}

export const eu8Countries = ['FR', 'GB', 'DE', 'ES', 'IT', 'NL', 'BE', 'AT'];
type BrandMinimumOrderValueSettingsType = 'discovery' | 'reorder';
export type BrandShippingFeeCorridor = 'domestic' | 'eu8' | 'international';

export interface BrandShippingFeeOption {
  amount: number;
  currency: Currency;
  isSpecialDeal: boolean;
  isDefault: boolean;
}

export interface BrandShippingFeeSetting {
  id: string;
  amount: number;
  corridor: BrandShippingFeeCorridor;
  currency: Currency;
  fee: number;
  type: 'franco-settings';
  meta: {
    amountOptions: BrandShippingFeeOption[];
    feeOptions: BrandShippingFeeOption[];
    defaultMaxFeeAmount: number;
  };
}

export type BrandShippingFeeSettingsApiResponse = JsonApiResponse<BrandShippingFeeSetting[]>;

export interface AddressesFormState {
  [AddressType.Billing]: AddressApiPayload;
  [AddressType.Public]: AddressApiPayload;
  [AddressType.Shipping]: AddressApiPayload;
  hasPublicAddress: boolean;
  hasShippingAddress: boolean;
}

export interface ShippingFormState {
  shippingFeeSettings: BrandShippingFeeSetting[];
  averageLeadTime: string;
  whitelistCountries: string[];
  eoriNumber: string;
  lucidNumber: string;
}

type PayloadKeys = keyof BrandShippingSettingsApiPayload;
export type BrandShippingSettingsApiErrors = { [Key in PayloadKeys]: string[] };

export interface AddressApiResourceResponse {
  shipping_address: Address;
  display_address: Address;
  billing_address: Address;
}

export enum DeliveryZone {
  Everywhere = 'everywhere',
  BrandCountry = 'brand_country',
  Custom = 'custom',
}

export interface CommunicationsConsentState {
  contact_opt_in: boolean | null;
  contact_opt_in_updated_at?: Date | null;
}

interface BrandCompanyDetailsApiBase {
  founded_in_year: number;
  company_name: string;
  tax_number: string;
  company_invoice_description: string;
  vat_number: string;
  vat_exemption: boolean;
  above_intracomm_threshold: boolean;
  instagram_username: string;
}

export interface BrandCompanyDetailsApiPayload extends BrandCompanyDetailsApiBase {
  is_sole_trader?: boolean;
  sole_trader_number?: string;
}

export interface BrandMinimumOrderValueSettingsApiPayload {
  amount: number;
  type: BrandMinimumOrderValueSettingsType;
}

export interface BrandMinimumOrderValueSettingsApiResponse {
  amount: number;
  type: BrandMinimumOrderValueSettingsType;
  currency: Currency;
}

export interface BrandCompanyDetailsApiResponse extends BrandCompanyDetailsApiBase {
  sole_trader_number: string;
}

export interface CompanyDetailsFormData {
  founded_in_year: number;
  company_name: string;
  tax_number: string;
  vat_number: string;
  vat_exemption: boolean;
  legal_form: string;
  registration_city: string;
  above_intracomm_threshold: boolean;
  is_sole_trader: boolean;
  sole_trader_number: string;
  instagram_username: string;
}

export enum AnkorpayProviderStatus {
  PENDING = 'pending',
  IN_REVIEW = 'in_review',
  ACCEPTED = 'accepted',
  REFUSED = 'refused',
}

export enum ExternalType {
  BRAND = 'brand',
  RETAILER = 'retailer',
}

interface AnkorpayBusinessProvider {
  type: 'ankorpay-business-provider';
  id: string;
  psp: string;
  status: AnkorpayProviderStatus;
  kycLink: string;
}

export interface AnkorpayBusiness {
  type: 'ankorpay-business';
  id: string;
  companyId: string;
  countryCode: string;
  externalId: string;
  providers: {
    data: AnkorpayBusinessProvider[];
  };
}

export interface GetAnkorpayBusinessParams {
  externalType: ExternalType;
  externalId: string;
  include?: string;
}
